import { computedValue, encounterCreatureField } from '../../shared/array-types.js';
import { compileVitality } from '../compile/vitality-build.js';
import { readItemField } from '../compile/item-field-reader.js';
import { createStatusStackReader } from '../compile/status-stack-reader.js';
import { statusRows } from '../statuses/expiry.js';
import contract from '../compile/c1-contract.json';

type Row = Record<string, unknown>;
const row = (value: unknown): Row => value && typeof value === 'object' && !Array.isArray(value) ? value as Row : {};
const text = (value: unknown): string => String(value ?? '');
const attackStacks = createStatusStackReader(contract.vitality.statusCatalog, {
    readStatuses: member => statusRows(member as Row, 'target'), readItemField,
});

/** Project only the changed vitality cluster from held inputs, including lean zero writes. */
export function attackVitalityDelta(member: Row, changes: Row): Row | null {
    if (changes.vitality) return { ...row(changes.vitality) };
    if (!['v_current', 'v_max', 'v_temp', 'v_max_temp'].some(key => key in changes)) return null;
    const vitality = row(row(member.computed).vitality), source: Row = {};
    for (const key of ['v_current', 'v_max', 'v_temp', 'v_max_temp'])
        source[key] = Number(changes[key] ?? computedValue(member, 'vitality', 'base_' + key)
            ?? computedValue(member, 'vitality', key) ?? encounterCreatureField(member.encounter_creatures, key) ?? 0);
    // The canonical builder copies only this small projection host, never the member.
    const projected = compileVitality({ items: { item_type: encounterCreatureField(member.encounter_creatures, 'item_type')
        ?? readItemField(member, 'item_type') }, computed: { vitality } }, source, {
        readVitality: () => null, readLayerValue: (value, key) => row(value)[key], readItemField,
        readStatuses: () => statusRows(member, 'target'),
        readBlightedStack: () => { const id = attackStacks.id('blighted'); return id ? attackStacks.read(member, id) : null; },
        buildStatusShell: id => {
            const entry = contract.vitality.vitalityCatalog.find(item => item.item_id === id);
            return entry ? { items: { item_id: entry.item_id, item_name: entry.item_name, item_type: entry.item_type } } : null;
        },
    });
    return row(row(projected.row).computed).vitality as Row;
}

/** The attack commit's decided rows and log-only markers, in attack_truth_commit_inc.php's order. */
export function localAttackRolls(command: Row, verdict: Row): Row[] {
    const rolls = { ...row(verdict.rolls ?? verdict), ...row(command.rolls) }, actor = text(command.actor_id), target = text(command.target_id);
    const item = text(command.ability_id || command.weapon_id), result: Row[] = [];
    const message = (kind: string, used: string, by = actor, to = target, privacy = command.roll_private): Row => {
        const entry: Row = {
            roll_type_1: kind, roll_type_2: null, roll_item_id: used, roll_by: by, roll_to: to, roll_private: privacy ?? 0,
            _local_used_item_id: used,
            dice: 0, die: 0, roll_mod: 0, roll_dc: 0, adv_dis_stacks: 0, rolls_full: null, roll_result: 0,
            roll_sound: { v: 2, voice: 0, ...(used === '' ? { used: 0 } : {}) },
        };
        result.push(entry); return entry;
    };
    const rolled = (value: unknown, used: string, by = actor, to = target, privacy = command.roll_private) => {
        if (!value || typeof value !== 'object' || Array.isArray(value)) return;
        const entry = row(value);
        result.push({ ...entry, roll_item_id: entry.roll_item_id ?? used, roll_by: by, roll_to: to,
            _local_used_item_id: used, roll_private: privacy ?? 0, roll_result: entry.result ?? entry.roll_result });
    };
    const list = (value: unknown): Row[] => Array.isArray(value) ? value.map(row) : [];
    const stray = row(command.stray);
    if (command.stray) {
        if (stray.unlucky) message('unlucky_roll', '', actor, '');
        if (stray.reaction_ability_id) message('reaction_ability_applied', text(stray.reaction_ability_id), text(stray.target_id), text(stray.target_id), 0);
        rolled(stray.roll, item, actor, text(stray.target_id));
        if (stray.double_natural_one) message('ranged_stray_double_natural_one', text(command.weapon_id), actor, target, 0)._local_used_item_id = item;
    }
    if (rolls.unlucky) message('unlucky_roll', '', actor, '');
    const reaction = row(rolls.reaction);
    if (reaction.ability_id) {
        // Only the paid Evade marker is inside PHP's janus_trigger_enter scope; a stray reaction is not.
        message('reaction_ability_applied', text(reaction.ability_id), target, target).triggered_effect_item_id = reaction.ability_id;
    }
    rolled(rolls.attack, item);
    if (rolls.miss ?? verdict.miss) message('weapon_attack_missed', item);
    else {
        for (const cascade of list(rolls.cascades)) rolled(cascade, text(command.weapon_id));
        rolled(rolls.damage, text(command.weapon_id));
    }
    const saves = Array.isArray(rolls.maimed_saves) ? list(rolls.maimed_saves) : rolls.maimed_save ? [row(rolls.maimed_save)] : [];
    const saved = (swing: string) => {
        for (const save of saves.filter(save => (save.swing ?? 'paid') === swing)) {
            if (save.unlucky) message('unlucky_roll', '', target, '', 0);
            rolled(save.row, text(command.weapon_id), target, '', 0);
        }
    };
    saved('paid');
    const rider = row(rolls.rider), riderItem = text(rider.weapon_id);
    if (rolls.rider) {
        if (rider.unlucky) message('unlucky_roll', '', actor, '');
        rolled(rider.attack, riderItem);
        if (rider.miss) message('weapon_attack_missed', riderItem);
        else {
            for (const cascade of list(rider.cascades)) rolled(cascade, riderItem);
            rolled(rider.damage, riderItem);
        }
    }
    saved('rider');
    // These insert_log_message calls are public and cite the inventory source, with the weapon's presentation identity.
    const move = row(command.inventory_move), change = row(command.inventory_change);
    if (move.inventory_id) message('drop_inventory', text(move.inventory_id), actor, target, 0)._local_used_item_id = text(command.weapon_id);
    if (change.inventory_id) message(text(change.log_type), text(change.inventory_id), actor, target, 0)._local_used_item_id = text(command.weapon_id);
    return result;
}

